import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { debugLogger } from '@/utils/debugLogger';
import { Profile } from '@/types/profile';

interface ProfileContextData {
  profile: Profile | null;
  verificationStatus: string | null;
  userRole: string;
  unreadNotifications: number;
  unreadMessages: number;
  loading: boolean;
  refetch: () => Promise<void>;
}

const ProfileContext = createContext<ProfileContextData | undefined>(undefined);

export const ProfileProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [verificationStatus, setVerificationStatus] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string>('user');
  const [unreadNotifications, setUnreadNotifications] = useState<number>(0);
  const [unreadMessages, setUnreadMessages] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  const fetchProfileData = async () => {
    if (!user?.id) {
      setProfile(null);
      setVerificationStatus(null);
      setUserRole('user');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // SINGLE RPC CALL - traz TUDO de uma vez (profile, verification, role, counters)
      const { data, error } = await supabase.rpc('get_my_profile_complete');

      if (error) throw error;
      if (!data || data.length === 0) throw new Error('No profile data');

      const result = data[0];
      
      // Parse profile JSON back to Profile object
      const fetchedProfile = result.profile as any;
      
      setProfile(fetchedProfile);
      setVerificationStatus(result.verification_status);
      setUserRole(result.user_role);
      setUnreadNotifications(result.unread_notifications);
      setUnreadMessages(result.unread_messages);

      // Pré-popular cache do AdminService
      if (fetchedProfile) {
        const { AdminService } = await import('@/core/services/AdminService');
        AdminService.preloadRolesCache([{
          id: user.id,
          user_role: result.user_role
        }]);
      }

      debugLogger.debug('[ProfileContext] Profile loaded via RPC', {
        userId: user.id,
        hasVerification: !!result.verification_status,
        role: result.user_role,
        unreadNotifications: result.unread_notifications,
        unreadMessages: result.unread_messages
      });
    } catch (error) {
      debugLogger.error('[ProfileContext] Failed to load profile:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfileData();
  }, [user?.id]);

  return (
    <ProfileContext.Provider value={{
      profile,
      verificationStatus,
      userRole,
      unreadNotifications,
      unreadMessages,
      loading,
      refetch: fetchProfileData
    }}>
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfile must be used within ProfileProvider');
  }
  return context;
};
